import { useState, useEffect } from "react";
import { States } from "../Settings/States";
import Select from 'react-select';
import { toast } from "react-toastify";
import Cookies from "universal-cookie";
import {
    FaAngleUp,
    FaAngleDown,
    FaWarehouse,
    FaEnvelope,
} from "react-icons/fa";
import { BsTelephoneFill } from "react-icons/bs";
import { MdLocationOn } from "react-icons/md";
import "../../stylesheets/Warehouse.scss";

export const DeliveryOrderWarehouse = (params) => {
    const {
        index,
        warehouseInfo = null,
        updateWarehouse,
        deleteWarehouse,
        isNew = true,
        emailError,
        closeModal,
        addWarehouse, // Function to add the new/updated warehouse to parent state
        setSelectedWarehouse // Function to set the selected warehouse in parent component
    } = params;

    const isEditMode = !isNew && Boolean(warehouseInfo);
    const [opened, setOpened] = useState(true);
    const [newName, setNewName] = useState(warehouseInfo?.name || "");
    const [displayName, setDisplayName] = useState(
        isEditMode ? `Edit ${warehouseInfo?.name || "Warehouse"}` : "New Warehouse"
    );
    const [newContact, setNewContact] = useState(
        warehouseInfo?.emails?.[0]?.name || warehouseInfo?.contact_name || ""
    );
    const [newEmail, setNewEmail] = useState(
        warehouseInfo?.emails?.[0]?.email || ""
    );
    const [newPhone, setNewPhone] = useState(warehouseInfo?.phone || "");
    const [newAddress, setNewAddress] = useState(warehouseInfo?.address || "");
    const [newCity, setNewCity] = useState(warehouseInfo?.city || "");
    const [newZip, setNewZip] = useState(warehouseInfo?.zip || "");
    const [state, setState] = useState(warehouseInfo?.state || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [existingWarehouses, setExistingWarehouses] = useState([]);
    const [fetching, setFetching] = useState(true);

    const URL = process.env.REACT_APP_API_URL + "/user/settings";
    const cookies = new Cookies();
    const token = cookies.get("token_user");

    const openTab = () => {
        setOpened(!opened);
    };

    const fetchUserSettings = async () => {
        setFetching(true);
        try {
            const response = await fetch(`${URL}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const data = await response.json();

            if (response.status === 200) {
                setExistingWarehouses(data.user?.warehouses || []);
            } else {
                setError("Failed to fetch user settings.");
            }
        } catch (e) {
            setError("An error occurred while fetching user settings.");
            console.error(e);
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchUserSettings();
    }, []);

    useEffect(() => {
        if (warehouseInfo) {
            setNewName(warehouseInfo?.name || "");
            setDisplayName(isEditMode ? `Edit ${warehouseInfo?.name || "Warehouse"}` : "New Warehouse");
            setNewContact(warehouseInfo?.emails?.[0]?.name || warehouseInfo?.contact_name || "");
            setNewEmail(warehouseInfo?.emails?.[0]?.email || "");
            setNewPhone(warehouseInfo?.phone || "");
            setNewAddress(warehouseInfo?.address || "");
            setNewCity(warehouseInfo?.city || "");
            setNewZip(warehouseInfo?.zip || "");
            setState(warehouseInfo?.state || "");
        }
    }, [warehouseInfo, isEditMode]);

    const saveWarehouse = async () => {
        if (!newName.trim()) {
            toast.error("Warehouse name is required");
            return;
        }

        setLoading(true);

        const warehousePayload = {
            ...(warehouseInfo?.id ? { id: warehouseInfo.id } : {}),
            name: newName.trim(),
            phone: newPhone.trim(),
            address: newAddress.trim(),
            city: newCity.trim(),
            state: state,
            zip: newZip.trim(),
            emails: [{ name: newContact.trim(), email: newEmail.trim() }]
        };

        let updatedList = [];
        const targetId = warehouseInfo?.id;
        const matchIndex = existingWarehouses.findIndex(
            (w) => (targetId ? w.id === targetId : w.name.trim().toLowerCase() === (warehouseInfo?.name || "").trim().toLowerCase())
        );

        if (isEditMode && matchIndex !== -1) {
            updatedList = existingWarehouses.map((w, idx) => {
                if (idx === matchIndex) {
                    return {
                        id: w.id,
                        ...warehousePayload,
                    };
                }
                return {
                    id: w.id,
                    name: w.name,
                    phone: w.phone || "",
                    address: w.address || "",
                    city: w.city || "",
                    state: w.state || "",
                    zip: w.zip || "",
                    emails: w.emails || [],
                };
            });
        } else {
            updatedList = [
                warehousePayload,
                ...existingWarehouses.map((w) => ({
                    id: w.id,
                    name: w.name,
                    phone: w.phone || "",
                    address: w.address || "",
                    city: w.city || "",
                    state: w.state || "",
                    zip: w.zip || "",
                    emails: w.emails || [],
                })),
            ];
        }

        const settingsObj = {
            warehouses: updatedList
        };

        try {
            const response = await fetch(`${URL}/save`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(settingsObj),
            });

            if (response.ok) {
                const result = await response.json();
                const successMsg = isEditMode ? "Warehouse updated successfully!" : "Warehouse created successfully!";
                toast.success(successMsg);

                let savedWarehouse = null;
                if (result.warehouses && Array.isArray(result.warehouses)) {
                    if (targetId) {
                        savedWarehouse = result.warehouses.find((w) => w.id === targetId);
                    }
                    if (!savedWarehouse) {
                        savedWarehouse = result.warehouses.find(
                            (w) => w.name.toLowerCase() === warehousePayload.name.toLowerCase()
                        );
                    }
                    if (!savedWarehouse) {
                        savedWarehouse = result.warehouses[0];
                    }
                }

                if (savedWarehouse && addWarehouse) {
                    addWarehouse(savedWarehouse);
                }
                setDisplayName(newName);
                if (closeModal) closeModal();
                if (typeof window.getShipmentData === "function") {
                    window.getShipmentData();
                }
            } else {
                toast.error(isEditMode ? "Failed to update warehouse" : "Failed to create warehouse");
            }
        } catch (e) {
            setError("Something went wrong. Please, try again in a few minutes!");
            toast.error("Something went wrong. Please, try again in a few minutes!");
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="individual-header" onClick={openTab} style={{ pointerEvents: fetching ? 'none' : 'auto', opacity: fetching ? 0.5 : 1 }}>
                <h3 className="name">
                    {displayName}
                </h3>
                <div className="arrow">
                    {opened ? <FaAngleUp /> : <FaAngleDown />}
                </div>
            </div>
            {opened && (
                <div className="warehouse-form" style={{ padding: "20px", maxWidth: "600px", backgroundColor: "#f4f4f4", borderRadius: "5px", pointerEvents: fetching ? 'none' : 'auto', opacity: fetching ? 0.5 : 1 }}>
                    {fetching ? (
                        <div style={{ textAlign: 'center' }}>Loading...</div>
                    ) : (
                        <>
                            <div className="input-section name-phone" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div className="input-container">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>Warehouse Name</label>
                                    <div className="input" style={{ position: "relative" }}>
                                        <FaWarehouse style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                        <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }} />
                                    </div>
                                </div>
                            </div>
                            <div className="input-section name-phone" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div className="input-container">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>Contact Name</label>
                                    <div className="input" style={{ position: "relative" }}>
                                        <FaWarehouse style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                        <input type="text" value={newContact} onChange={(e) => setNewContact(e.target.value)} style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }} />
                                    </div>
                                </div>
                                <div className="input-container">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>Contact Email</label>
                                    <div className="input" style={{ position: "relative" }}>
                                        <FaEnvelope style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                        <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Contact Email" style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }} />
                                    </div>
                                </div>
                                <div className="input-container phone">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>Phone Number</label>
                                    <div className="input" style={{ position: "relative" }}>
                                        <BsTelephoneFill style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                        <input type="text" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Phone Number" style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }} />
                                    </div>
                                </div>
                            </div>
                            <div className="input-section address" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div className="input-container street">
                                    <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>Street Address</label>
                                    <div className="input" style={{ position: "relative" }}>
                                        <MdLocationOn style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "grey" }} />
                                        <input type="text" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} style={{ paddingLeft: "35px", flex: "1", padding: "10px 10px 10px 35px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }} />
                                    </div>
                                </div>
                                <div className="input-section address-details" style={{ display: "flex", gap: "10px" }}>
                                    <div className="input-container city" style={{ flex: 1 }}>
                                        <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>City</label>
                                        <div className="input">
                                            <input type="text" value={newCity} onChange={(e) => setNewCity(e.target.value)} style={{ paddingLeft: "10px", flex: "1", padding: "10px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }} />
                                        </div>
                                    </div>
                                    <div className="input-container state" style={{ flex: 1.5 }}>
                                        <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>State</label>
                                        <Select
                                            options={States.map(state => ({ value: state?.abbreviation, label: state?.abbreviation })).sort((a, b) => (a.label || "").localeCompare(b.label || ""))}
                                            onChange={(e) => setState(e?.value || "")}
                                            value={state ? { value: state, label: state } : null}
                                            className="select"
                                            styles={{ container: (base) => ({ ...base, flex: 1 }) }}
                                        />
                                    </div>
                                    <div className="input-container zip" style={{ flex: 1 }}>
                                        <label className="input-label" style={{ fontSize: "14px", marginBottom: "5px" }}>ZIP</label>
                                        <div className="input">
                                            <input type="text" value={newZip} onChange={(e) => setNewZip(e.target.value)} style={{ paddingLeft: "10px", flex: "1", padding: "10px", backgroundColor: "white", borderRadius: "5px", border: "1px solid #d3d3d3" }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {error && <p className="error-message">{error}</p>}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
                                <button className="create-warehouse" onClick={saveWarehouse} disabled={loading} style={{ backgroundColor: "var(--green)", width: "46%", border: "none", color: "white", borderRadius: "5px", padding: "5px 10px", height: "fit-content" }}>
                                    {loading ? (isEditMode ? "Saving..." : "Creating...") : (isEditMode ? "Save Warehouse" : "Create Warehouse")}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default DeliveryOrderWarehouse;
